<template>
  <div class="car-statistics">
    <Spin v-if="loading" fix></Spin>

    <!-- 统计概览卡片 -->
    <Row :gutter="16" class="stats-overview">
      <Col span="6" v-for="item in overviewCards" :key="item.key">
        <Card class="stat-card">
          <div class="stat-icon" :style="{ backgroundColor: item.color }">
            <Icon :type="item.icon" size="24" color="#fff" />
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ item.value }}</div>
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-trend" v-if="item.growth !== undefined">
              <span :class="item.growth >= 0 ? 'up' : 'down'">
                <Icon :type="item.growth >= 0 ? 'ios-arrow-up' : 'ios-arrow-down'" />
                {{ Math.abs(item.growth) }}%
              </span>
              <span class="trend-desc">较上期</span>
            </div>
          </div>
        </Card>
      </Col>
    </Row>

    <!-- 图表区域 -->
    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="16">
        <Card title="车辆使用趋势">
          <div ref="usageTrendChart" style="height: 350px;"></div>
        </Card>
      </Col>
      <Col span="8">
        <Card title="车辆状态分布">
          <div ref="carStatusChart" style="height: 350px;"></div>
        </Card>
      </Col>
    </Row>

    <Row :gutter="16" style="margin-top: 16px;">
      <Col span="12">
        <Card title="区域车辆分布">
          <div ref="areaCarChart" style="height: 300px;"></div>
        </Card>
      </Col>
      <Col span="12">
        <Card title="车辆收益排行">
          <div ref="carRevenueChart" style="height: 300px;"></div>
        </Card>
      </Col>
    </Row>

    <!-- 车辆数据表格 -->
    <Card title="车辆详细统计" style="margin-top: 16px;">
      <Table :columns="tableColumns" :data="tableData" border stripe />
    </Card>
  </div>
</template>

<script>
import { getCarStatistics } from '@/api/statistics'
import Echarts from 'echarts'

export default {
  name: 'CarStatistics',
  props: {
    dateRange: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      statsData: null,
      usageTrendChart: null,
      carStatusChart: null,
      areaCarChart: null,
      carRevenueChart: null,
      tableColumns: [
        { title: '车辆编号', key: 'carId', width: 120 },
        { title: '车辆名称', key: 'carName', width: 150 },
        { title: '所属区域', key: 'areaName', width: 120 },
        { title: '订单数', key: 'orderCount', width: 100 },
        { title: '使用时长(小时)', key: 'usageHours', width: 120, render: (h, p) => h('span', (p.row.usageHours || 0).toFixed(1)) },
        { title: '收益', key: 'revenue', width: 120, render: (h, p) => h('span', { style: { color: '#f56c6c' } }, `¥${(p.row.revenue || 0).toFixed(2)}`) },
        { title: '使用率', key: 'usageRate', width: 100, render: (h, p) => h('span', `${((p.row.usageRate || 0) * 100).toFixed(1)}%`) }
      ],
      tableData: []
    }
  },
  computed: {
    overviewCards() {
      if (!this.statsData) return []
      const data = this.statsData
      return [
        {
          key: 'total',
          label: '总车辆数',
          value: data.totalCars || 0,
          icon: 'ios-car',
          color: '#409EFF'
        },
        {
          key: 'online',
          label: '在线车辆',
          value: data.onlineCars || 0,
          growth: data.onlineGrowth,
          icon: 'ios-wifi',
          color: '#67C23A'
        },
        {
          key: 'usage',
          label: '平均使用率',
          value: `${((data.avgUsageRate || 0) * 100).toFixed(1)}%`,
          growth: data.usageGrowth,
          icon: 'ios-speedometer',
          color: '#E6A23C'
        },
        {
          key: 'revenue',
          label: '车辆总收益',
          value: `¥${this.formatMoney(data.totalRevenue || 0)}`,
          growth: data.revenueGrowth,
          icon: 'logo-yen',
          color: '#F56C6C'
        }
      ]
    }
  },
  watch: {
    dateRange: {
      handler() {
        this.loadData()
      },
      deep: true
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          startDate: this.dateRange[0],
          endDate: this.dateRange[1]
        }
        
        const res = await getCarStatistics(params)
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.statsData = res.data.data
          this.tableData = this.statsData.carDetails || []
          this.$nextTick(() => {
            this.initCharts()
          })
        } else {
          this.$Message.error(res.data?.message || '获取车辆统计数据失败')
        }
      } catch (error) {
        this.$Message.error('获取车辆统计数据失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    initCharts() {
      this.initUsageTrendChart()
      this.initCarStatusChart()
      this.initAreaCarChart()
      this.initCarRevenueChart()
    },
    initUsageTrendChart() {
      if (!this.$refs.usageTrendChart || !this.statsData) return
      
      if (this.usageTrendChart) {
        this.usageTrendChart.dispose()
      }
      
      this.usageTrendChart = Echarts.init(this.$refs.usageTrendChart)
      const trendData = this.statsData.usageTrend || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' }
        },
        legend: {
          data: ['使用次数', '使用时长', '收益']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trendData.map(item => item.date)
        },
        yAxis: [
          {
            type: 'value',
            name: '使用次数/时长',
            position: 'left'
          },
          {
            type: 'value',
            name: '收益(元)',
            position: 'right'
          }
        ],
        series: [
          {
            name: '使用次数',
            type: 'bar',
            data: trendData.map(item => item.usageCount),
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '使用时长',
            type: 'bar',
            data: trendData.map(item => item.usageHours),
            itemStyle: { color: '#67C23A' }
          },
          {
            name: '收益',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            data: trendData.map(item => item.revenue),
            itemStyle: { color: '#F56C6C' }
          }
        ]
      }
      
      this.usageTrendChart.setOption(option)
    },
    initCarStatusChart() {
      if (!this.$refs.carStatusChart || !this.statsData) return
      
      if (this.carStatusChart) {
        this.carStatusChart.dispose()
      }
      
      this.carStatusChart = Echarts.init(this.$refs.carStatusChart)
      const statusData = this.statsData.statusDistribution || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 20,
          data: statusData.map(item => item.name)
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: statusData
          }
        ],
        color: ['#67C23A', '#409EFF', '#E6A23C', '#F56C6C', '#909399']
      }
      
      this.carStatusChart.setOption(option)
    },
    initAreaCarChart() {
      if (!this.$refs.areaCarChart || !this.statsData) return
      
      if (this.areaCarChart) {
        this.areaCarChart.dispose()
      }
      
      this.areaCarChart = Echarts.init(this.$refs.areaCarChart)
      const areaData = this.statsData.areaDistribution || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: areaData.map(item => item.areaName),
          axisLabel: {
            rotate: 30,
            interval: 0
          }
        },
        yAxis: {
          type: 'value'
        },
        series: [
          {
            type: 'bar',
            data: areaData.map(item => ({
              value: item.carCount,
              itemStyle: {
                color: new Echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#83bff6' },
                  { offset: 0.5, color: '#188df0' },
                  { offset: 1, color: '#188df0' }
                ])
              }
            })),
            barWidth: '50%'
          }
        ]
      }
      
      this.areaCarChart.setOption(option)
    },
    initCarRevenueChart() {
      if (!this.$refs.carRevenueChart || !this.statsData) return
      
      if (this.carRevenueChart) {
        this.carRevenueChart.dispose()
      }
      
      this.carRevenueChart = Echarts.init(this.$refs.carRevenueChart)
      const revenueData = (this.statsData.topRevenueCars || []).slice(0, 10)
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            return `${params[0].name}<br/>收益: ¥${params[0].value.toFixed(2)}`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => `¥${value >= 10000 ? (value / 10000).toFixed(1) + '万' : value}`
          }
        },
        yAxis: {
          type: 'category',
          data: revenueData.map(item => item.carName),
          inverse: true
        },
        series: [
          {
            type: 'bar',
            data: revenueData.map(item => item.revenue),
            itemStyle: {
              color: new Echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: '#F56C6C' },
                { offset: 1, color: '#F9A7A7' }
              ])
            }
          }
        ]
      }
      
      this.carRevenueChart.setOption(option)
    },
    formatMoney(value) {
      if (value >= 100000000) {
        return (value / 100000000).toFixed(2) + '亿'
      }
      if (value >= 10000) {
        return (value / 10000).toFixed(2) + '万'
      }
      return value.toFixed(2)
    }
  },
  mounted() {
    this.loadData()
    window.addEventListener('resize', () => {
      this.usageTrendChart?.resize()
      this.carStatusChart?.resize()
      this.areaCarChart?.resize()
      this.carRevenueChart?.resize()
    })
  },
  beforeDestroy() {
    this.usageTrendChart?.dispose()
    this.carStatusChart?.dispose()
    this.areaCarChart?.dispose()
    this.carRevenueChart?.dispose()
  }
}
</script>

<style lang="less" scoped>
.car-statistics {
  .stats-overview {
    .stat-card {
      display: flex;
      align-items: center;
      padding: 20px;
      
      .stat-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
      }
      
      .stat-content {
        flex: 1;
        
        .stat-value {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1.2;
        }
        
        .stat-label {
          font-size: 14px;
          color: #909399;
          margin-top: 4px;
        }
        
        .stat-trend {
          font-size: 12px;
          margin-top: 8px;
          
          .up {
            color: #67C23A;
          }
          
          .down {
            color: #F56C6C;
          }
          
          .trend-desc {
            color: #909399;
            margin-left: 4px;
          }
        }
      }
    }
  }
}
</style>

